import {
  ApiContact,
  ApiCustomer,
  ApiCustomerAccount,
  ApiCustomerAccountDetailsParams,
  ApiCustomerContactParams,
  ApiCustomerData,
  ApiCustomerDataNote,
  ApiCustomerDataParams,
  ApiCustomerDetails,
  ApiCustomerOrderFilter,
} from "./interface.api";
import { CONTACT_TYPE, Customer, CustomerAccountDetails, CustomerAddress, CustomerContacts, CustomerData, OrderNote } from "@/types";

import { OrderFilterParams } from "../order/interface";
import { mapOrderFilter } from "../order/mappers";

export const accountDetailsParamMapper = (accountDetails: CustomerAccountDetails, address: CustomerAddress): ApiCustomerAccountDetailsParams => {
  return {
    AccountType: accountDetails.accountType,
    BranchName: accountDetails.branchName,
    CompanyName: address.name,
    ConfirmPassword: accountDetails.confirmPassword || '',
    Email: accountDetails.email,
    FirstName: accountDetails.firstName,
    LastName: accountDetails.lastName,
    Password: accountDetails.password || '',
    PhoneNumber: accountDetails.accountPhoneNumber || '',
    SendEmailNotification: accountDetails.sendEmailNotification || false,
    Website: accountDetails.website
  }
}

export const accountDetailsMapper = (api: ApiCustomerAccount, branch: string | null): CustomerAccountDetails => {
  return {
    accountPhoneNumber: api.AccountPhoneNumber,
    accountType: '',
    branchName: branch,
    daysToPayInvoices: api.DaysToPayInvoices,
    email: api.AccountsEmailAddress,
    firstName: api.FirstName,
    isActive: api.IsActive,
    lastName: api.LastName,
    login: api.Name,
    sendEmailNotification: api.SendEmailNotification,
    website: api.Website,
  }
}

export const addressMapper = (api: ApiCustomerDetails): CustomerAddress => {
  return {
    active: api.IsActive,
    area: api.Area,
    countryId: api.CountryId,
    county: api.Country,
    customerCode: api.CustomerCode,
    houseName: api.HouseName,
    houseNumber: api.HouseNumber,
    isCardPayment: api.IsCardPayment,
    isLowQuality: api.IsLowQuality,
    name: api.Name,
    postcode: api.Postcode,
    road: api.Road,
    shouldRemoveData: api.ShouldRemoveData,
    tax: api.IsTaxAdded,
    town: api.Town
  }
}

export const customerContactsParamMapper = (contacts: CustomerContacts[]): ApiCustomerContactParams => {
  return {
    EmailAddress: contacts[0].email,
    Name: contacts[0].name,
    PhoneNumber1: contacts[0].phone1,
    PhoneNumber2: contacts[0].phone2,
  };
}

export const contactsParamMapper = (api: ApiContact[]): CustomerContacts[] => {
  return api.map((contact) => ({
    contactId: `${contact.ContactId}`,
    customerId: `${contact.CustomerId}`,
    email: contact.EmailAddress,
    name: contact.Name,
    phone1: contact.PhoneNumber1 || '',
    phone2: contact.PhoneNumber2 || '',
    sendInvoice: contact.SendInvoice,
    type: CONTACT_TYPE.AGENT
  }));
}

export const customerDetailsParamMapper = (details: CustomerAddress, branchName: string | null): ApiCustomerDetails => {
  return {
    Area: details.area,
    Branch: branchName,
    Country: details.county,
    CountryId: details.countryId,
    CustomerCode: details.customerCode,
    HouseName: details.houseName,
    HouseNumber: details.houseNumber,
    IsActive: details.active,
    IsCardPayment: details.isCardPayment,
    IsLowQuality: details.isLowQuality,
    IsTaxAdded: details.tax,
    Name: details.name,
    Postcode: details.postcode,
    Road: details.road,
    ShouldRemoveData: details.shouldRemoveData,
    Town: details.town,
  }
}

// export const customerNotesParamMapper = (notes: CustomerNotes): ApiCustomerNotes => {
//   return {
//     Notes: notes.notes,
//   }
// }

export const customerDataParamsMapper = (data: CustomerData): ApiCustomerDataParams => {
  return {
    AccountDetails: accountDetailsParamMapper(data.accountDetails, data.address),
    Contacts: customerContactsParamMapper(data.contacts),
    Details: customerDetailsParamMapper(data.address, data.accountDetails.branchName),
    Notes: {},
  }
}

export const customerDataApiMapper = (api: ApiCustomerData): CustomerData => {
  return {
    accountDetails: accountDetailsMapper(api.AccountDetails, api.Details.Branch || ''),
    address: addressMapper(api.Details),
    contacts: contactsParamMapper(api.Contacts),
  }
}

export const customerApiMapper = (api: ApiCustomer): Customer => {
  return {
    active: api.IsActive,
    address: api.Address,
    branchId: api.BranchId,
    branchName: api.Branch,
    code: api.Code,
    companyName: api.Name,
    email: api.EmailAddress,
    firstName: api.FirstName,
    id: api.ApplicationUserId,
    invited: api.Invited === 'yes',
    lastName: api.LastName,
    phone: api.AccountPhoneNumber,
    upsell: `${api.UpsellPrice}`,
    website: api.Website,
  }
}

export const customersApiMapper = (api: ApiCustomer[]): Customer[] => {
  return api.map((customer) => customerApiMapper(customer));
};

export const customerOrderFilterMapper = (api: OrderFilterParams): ApiCustomerOrderFilter => {
  return {
    ...mapOrderFilter(api),
    CustomerId: Number(api.customerId) || null,
  };
}

export const customerApiDataNoteMapper = (api: ApiCustomerDataNote): OrderNote => {
  return {
    content: api.Content,
    created: api.CreatedDateTime,
    id: api.NoteId.toString(),
    owner: api.OwnerName,
  };
}

export const customerApiDataNotesMapper = (api: ApiCustomerDataNote[]): OrderNote[] => {
  return api.map((note) => customerApiDataNoteMapper(note));
};